import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { filter, first, includes, indexOf, map } from 'es-toolkit/compat';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { getChannelMarkers, getChannelMarkersQueryKey } from 'api/markerTypes';
import { runMultiplexCellSegmentation } from 'api/platform';
import { getMultiplexDnaStainTypeData } from 'interfaces/stainType';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useCasesParams } from 'utils/useCasesParams';
import { CasesParams } from 'utils/useCasesParams/types';
import { ActionStepper } from '../../ActionStepper';
import { ExecutionRequirementsPanel } from '../../requirements/ExecutionRequirementsPanel';
import { ExecutionRequirementType, useExecutionRequirements } from '../../requirements/useExecutionRequirements';
import { JobNameAndDescriptionStep } from '../../utils';
import {
  buildCohortWideChannelSchema,
  getChannelMarkersInAllSlides,
  getCohortWideAllowedChannelNames,
} from '../channelSelection';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { ConfigurationStep } from './ConfigurationStep';
import {
  IFormValues,
  MultiplexCellSegmentationModelTypes,
  RunMultiplexCellSegmentationSteps,
  runMultiplexCellSegmentationStepsOrder,
} from './types';

const SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_SEGMENTATION = 'RUN_MULTIPLEX_CELL_SEGMENTATION';

export interface IFormProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams?: CasesParams;
}

export const RunMultiplexCellSegmentation = (props: IFormProps): ReactElement => {
  const { onClose, casesParams: casesParamsOverride } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [activeStepIndex, setActiveStep] = useState(0);
  const activeStep = runMultiplexCellSegmentationStepsOrder[activeStepIndex];
  const { casesParams: urlCasesParams } = useCasesParams();
  const casesParams = casesParamsOverride || urlCasesParams;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;

  const { data: channelMarkersResponse } = useQuery({
    queryKey: getChannelMarkersQueryKey(multiplexCasesParams),
    queryFn: ({ signal }) => getChannelMarkers(multiplexCasesParams, signal),
    enabled: Boolean(effectiveCasesParams),
  });
  const { stainTypeOptionsWithoutDeprecated } = useStainTypeOptions();

  const channelMarkersInAllSlides = useMemo(
    () => getChannelMarkersInAllSlides(channelMarkersResponse),
    [channelMarkersResponse]
  );

  const { dnaStainTypeIds } = useMemo(() => {
    return getMultiplexDnaStainTypeData(stainTypeOptionsWithoutDeprecated);
  }, [stainTypeOptionsWithoutDeprecated]);

  const nuclearChannelNames = useMemo(
    () => getCohortWideAllowedChannelNames(channelMarkersInAllSlides, dnaStainTypeIds),
    [channelMarkersInAllSlides, dnaStainTypeIds]
  );
  const nonNuclearChannelNames = useMemo(
    () => filter(channelMarkersInAllSlides, (channelName) => !includes(dnaStainTypeIds, channelName)),
    [channelMarkersInAllSlides, dnaStainTypeIds]
  );

  const defaultNuclearChannelName = useMemo(() => first(nuclearChannelNames), [nuclearChannelNames]);

  const defaultValues: Partial<IFormValues> = useMemo(() => {
    return {
      modelType: MultiplexCellSegmentationModelTypes.amikta,
      nuclearChannelNames: [],
      membranalChannelNames: [] as string[],
    };
  }, []);

  const nuclearChannelSelectSchema = useMemo(() => {
    return buildCohortWideChannelSchema(nuclearChannelNames, {
      inputLabel: 'Nuclear channel',
    });
  }, [nuclearChannelNames]);
  const nonNuclearChannelSelectSchema = useMemo(() => {
    return buildCohortWideChannelSchema(nonNuclearChannelNames, {
      inputLabel: 'Non-nuclear channel',
    });
  }, [nonNuclearChannelNames]);

  const validationSchema = useMemo(
    () => ({
      [RunMultiplexCellSegmentationSteps.NameAndDescription]: yup.object({
        jobName: yup.string(),
        jobDescription: yup.string(),
      }),
      [RunMultiplexCellSegmentationSteps.Configuration]: yup.object({
        modelType: yup.string().required(),
        nuclearChannelNames: nuclearChannelSelectSchema.min(1, 'At least one nuclear channel is required'),
        membranalChannelNames: nonNuclearChannelSelectSchema,
        modelPath: yup.string().when('modelType', {
          is: MultiplexCellSegmentationModelTypes.amikta,
          then: yup.string().required(),
          otherwise: yup.string().notRequired().nullable(),
        }),
      }),
    }),
    [nonNuclearChannelSelectSchema, nuclearChannelSelectSchema]
  );

  const currentValidationSchema = validationSchema[activeStep];

  const methods = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = methods;
  const executionRequirements = useExecutionRequirements([
    {
      type: ExecutionRequirementType.CloudOptimizedSlides,
      label: 'Cloud-optimized slides',
      casesParams: multiplexCasesParams,
      slideIds: multiplexCasesParams.slideIdsToInclude,
      enabled: Boolean(multiplexCasesParams.studyId || multiplexCasesParams.filters?.studyId),
    },
  ]);

  const runMultiplexCellSegmentationMutation = useMutation(runMultiplexCellSegmentation, {
    onError: () => {
      enqueueSnackbar('Error occurred, Multiplex Cell Segmentation failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex Cell Segmentation Started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_SEGMENTATION);
    },
  });

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Multiplex Cell Segmentation to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_SEGMENTATION,
      autoHideDuration: null,
    });

    await runMultiplexCellSegmentationMutation.mutateAsync({
      ...multiplexCasesParams,
      configParams: { ...data },
    });
  };

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>();

  const checkValidationAndSetIsStepFailed = (
    step: RunMultiplexCellSegmentationSteps,
    objectToValidate: Record<string, any>
  ) => {
    validationSchema[step]
      .validate(objectToValidate)
      .then(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [indexOf(runMultiplexCellSegmentationStepsOrder, step)]: false,
        }));
      })
      .catch((err) => {
        console.error(err);
        setIsStepFailed((prev) => ({
          ...prev,
          [indexOf(runMultiplexCellSegmentationStepsOrder, step)]: true,
        }));
      });
  };

  const steps = {
    [RunMultiplexCellSegmentationSteps.NameAndDescription]: {
      label: RunMultiplexCellSegmentationSteps.NameAndDescription,
      subLabel: activeStep !== RunMultiplexCellSegmentationSteps.NameAndDescription && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
      onNextOrBackClick: () =>
        checkValidationAndSetIsStepFailed(RunMultiplexCellSegmentationSteps.NameAndDescription, {
          modelType: watch('modelType'),
          nuclearChannelNames: watch('nuclearChannelNames'),
          membranalChannelNames: watch('membranalChannelNames'),
        }),
    },
    [RunMultiplexCellSegmentationSteps.Configuration]: {
      label: RunMultiplexCellSegmentationSteps.Configuration,
      content: (
        <ConfigurationStep
          casesParams={multiplexCasesParams}
          methods={methods}
          nuclearChannelNames={nuclearChannelNames}
          nonNuclearChannelNames={nonNuclearChannelNames}
          defaultNuclearChannelName={defaultNuclearChannelName}
          checkValidationAndSetIsStepFailed={checkValidationAndSetIsStepFailed}
        />
      ),
    },
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ExecutionRequirementsPanel requirements={executionRequirements.requirements} />
      </Grid>
      <Grid item>
        <ActionStepper
          hideExecution={!executionRequirements.canExecute}
          handleSubmit={handleSubmit(onSubmit)}
          steps={map(runMultiplexCellSegmentationStepsOrder, (step) => steps[step])}
          setActiveStepForValidation={setActiveStep}
          isStepFailed={isStepFailed}
        />
      </Grid>
    </Grid>
  );
};
